import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BannerService } from '@techspert-io/growthbook';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs';
import {
  IDataCaptureSegment,
  IDataCaptureSheet,
  IDiscussionGuideQuestion,
  SuggestionStatus,
} from '../../models/data-capture.models';
import { DataCaptureService } from '../../services/data-capture.service';
import { QuestionHierarchyService } from '../../services/question-hierarchy.service';
import { ThemeValidationService } from '../../services/theme-validator.service';
import {
  sheetNameValidator,
  trimmedLengthValidator,
  trimSheetName,
} from '../../validators/sheet-name.validators';
import { FormGroupQuestion } from '../re-orderable-theme/question-node/question-node.component';
import { FormGroupTheme } from '../re-orderable-theme/re-orderable-theme.component';
import { EditSheetConfirmationComponent } from './edit-sheet-confirmation/edit-sheet-confirmation.component';

interface IEditFormGroup {
  reorderablePanel: FormGroup<{
    themes: FormArray<FormGroupTheme>;
  }>;
  segments: FormControl<string[]>;
  expertName: FormControl<boolean>;
  expertBio: FormControl<boolean>;
  dateAndTimeOfCall: FormControl<boolean>;
  geography: FormControl<boolean>;
  profileType: FormControl<boolean>;
  interviewerName: FormControl<boolean>;
  notetakerName: FormControl<boolean>;
  recruitmentSource: FormControl<boolean>;
  answerType: FormControl<'summarised' | 'verbatim'>;
  suggestedQuestionThreshold: FormControl<number>;
}

@Component({
  selector: 'app-edit-sheet',
  templateUrl: './edit-sheet.component.html',
  styleUrls: ['./edit-sheet.component.scss'],
  standalone: false,
})
export class EditSheetComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  nameControl: FormControl<string>;
  editFormGroup: FormGroup<IEditFormGroup>;

  segments: IDataCaptureSegment[] = [];

  loading = true;
  submitting = false;

  editFormSubmitted = false;

  sheet: IDataCaptureSheet;
  sheetNames: string[];
  publicDisplayId: string;

  suggestedQuestionCount: number | null = null;
  loadingSuggestedCount = false;

  suggestedQuestionsByTheme: Record<string, IDiscussionGuideQuestion[]> = {};

  get themesControl() {
    return this.editFormGroup.controls.reorderablePanel.controls.themes;
  }

  get sheetSegmentIds() {
    return this.sheet.opportunity_segment_ids;
  }

  get sheetHeight() {
    return `calc(100vh - 88px - ${this.bannerService.banner ? 48 : 0}px)`;
  }

  get questionHeight() {
    return `calc(100vh - 88px - 64px - 29px - 4rem - 36px - ${this.bannerService.banner ? 48 : 0}px)`;
  }

  constructor(
    private dataCaptureService: DataCaptureService,
    private activatedRoute: ActivatedRoute,
    private bannerService: BannerService,
    private questionHierarchyService: QuestionHierarchyService,
    private themeValidatorService: ThemeValidationService,
    private router: Router,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.activatedRoute.paramMap
      .pipe(
        map((params) => ({
          sheetId: params.get('sheetId'),
          publicDisplayId: params.get('publicDisplayId'),
        })),
        switchMap(({ sheetId, publicDisplayId }) =>
          this.dataCaptureService
            .getSheet(sheetId)
            .pipe(map((sheet) => ({ sheet, publicDisplayId })))
        ),
        switchMap(({ sheet, publicDisplayId }) =>
          combineLatest([
            this.dataCaptureService.getAvailableSegments(sheet.opportunity_id),
            this.dataCaptureService.querySheetsByOpportunityId(
              sheet.opportunity_id
            ),
          ]).pipe(
            map(([segments, sheets]) => ({
              sheet,
              segments,
              sheets,
              publicDisplayId,
            }))
          )
        )
      )
      .subscribe(({ sheet, segments, sheets, publicDisplayId }) => {
        this.sheet = sheet;
        this.segments = segments;
        this.publicDisplayId = publicDisplayId;
        this.sheetNames = sheets
          .filter(
            (s) => s.data_capture_sheet_id !== sheet.data_capture_sheet_id
          )
          .map((s) => s.name);

        this.initialiseForms(sheet);

        this.loading = false;
      });
  }

  confirmChange() {
    this.editFormSubmitted = true;

    if (this.editFormGroup.invalid || this.nameControl.invalid) {
      this.editFormGroup.markAllAsTouched();
      this.nameControl.markAsTouched();
      return;
    }

    const {
      segments,
      expertName,
      expertBio,
      dateAndTimeOfCall,
      geography,
      profileType,
      interviewerName,
      notetakerName,
      recruitmentSource,
      answerType,
      suggestedQuestionThreshold,
    } = this.editFormGroup.getRawValue();

    const modifiedQuestions = this.questionHierarchyService
      .flattenThemes(
        this.editFormGroup.controls.reorderablePanel.controls.themes
      )
      .filter((formQuestion) => {
        const originalQuestion = this.sheet.discussion_guide.find(
          (q) => q.question_id === formQuestion.question_id
        );

        return (
          originalQuestion &&
          originalQuestion.question !== formQuestion.question
        );
      });

    const editSheetImpl$ = (rebuildQuestionIds: string[] = []) =>
      this.dataCaptureService.editSheet({
        ...this.sheet,
        name: trimSheetName(this.nameControl.value),
        discussion_guide: this.buildDiscussionGuidePayload(),
        opportunity_segment_ids: segments,
        answer_format: answerType,
        suggested_question_threshold: suggestedQuestionThreshold,
        additional_export_headers: {
          expert_name: expertName,
          expert_bio: expertBio,
          date_and_time_of_call: dateAndTimeOfCall,
          geography,
          profile_type: profileType,
          interviewer_name: interviewerName,
          notetaker_name: notetakerName,
          respondent_source: recruitmentSource,
        },
        rebuild_question_ids: rebuildQuestionIds,
      });

    if (modifiedQuestions.length) {
      this.dialog
        .open<
          EditSheetConfirmationComponent,
          IDiscussionGuideQuestion[],
          string[]
        >(EditSheetConfirmationComponent, {
          width: '600px',
          data: modifiedQuestions,
          disableClose: true,
          autoFocus: false,
        })
        .afterClosed()
        .pipe(
          takeWhile((result) => !!result),
          tap(() => {
            this.submitting = true;
          }),
          switchMap((modifiedQuestionIds) =>
            editSheetImpl$(modifiedQuestionIds)
          )
        )
        .subscribe(() => {
          this.submitting = false;
          this.close();
        });
    } else {
      this.submitting = true;

      editSheetImpl$().subscribe(() => {
        this.submitting = false;
        this.close();
      });
    }
  }

  updateSuggestionStatus(questionId: string, status: SuggestionStatus) {
    const suggestion = Object.values(this.suggestedQuestionsByTheme)
      .flat()
      .find((question) => question.question_id === questionId);

    if (!suggestion) {
      return;
    }

    if (
      status === 'approved' &&
      suggestion.parent_question_id &&
      !this.findQuestionInThemes(suggestion.parent_question_id)
    ) {
      this.toastr.error(
        'Approve the parent question before approving a follow-up',
        'Unable to approve suggestion'
      );
      return;
    }

    this.dataCaptureService
      .updateSuggestionStatus(
        this.sheet.data_capture_sheet_id,
        questionId,
        status
      )
      .pipe(
        tap((updatedSheet) => {
          this.sheet = updatedSheet;
          this.removeSuggestedQuestion(questionId);

          if (status === 'approved') {
            const approvedQuestion = updatedSheet.discussion_guide.find(
              (question) => question.question_id === questionId
            );

            if (approvedQuestion) {
              this.insertApprovedSuggestion(approvedQuestion);
            }
          }
        }),
        catchError(() => {
          this.toastr.error(
            'Please try again',
            `Error ${status === 'approved' ? 'approving' : 'declining'} suggestion`
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close() {
    this.router.navigate(['/connect', this.publicDisplayId], {
      queryParams: {
        tab: 'dataCapture',
      },
    });
  }

  private initialiseForms(sheet: IDataCaptureSheet) {
    this.nameControl = new FormControl<string>(sheet.name, {
      validators: [
        trimmedLengthValidator(),
        sheetNameValidator(() => this.sheetNames),
      ],
      nonNullable: true,
    });

    const editableQuestions = sheet.discussion_guide.filter(
      (q) =>
        q.suggestion_status !== 'suggested' &&
        q.suggestion_status !== 'declined'
    );
    const suggestedQuestions = sheet.discussion_guide.filter(
      (q) => q.suggestion_status === 'suggested'
    );

    this.suggestedQuestionsByTheme = suggestedQuestions.reduce<
      Record<string, IDiscussionGuideQuestion[]>
    >(
      (acc, q) => ({
        ...acc,
        [q.theme]: [...(acc[q.theme] || []), q],
      }),
      {}
    );

    const themes = this.groupQuestionsByTheme(editableQuestions);

    this.editFormGroup = new FormGroup<IEditFormGroup>({
      reorderablePanel: new FormGroup({
        themes: new FormArray<FormGroupTheme>(
          themes.map(
            (theme) =>
              new FormGroup({
                theme: new FormControl<string>(theme.theme),
                questions: new FormArray<FormGroupQuestion>(
                  (theme.questions || [])
                    .filter((q) => !q.parent_question_id)
                    .map(
                      (q) =>
                        new FormGroup({
                          question_id: new FormControl<string>(q.question_id),
                          question: new FormControl<string>(q.question),
                          theme: new FormControl<string>(q.theme),
                          label: new FormControl<string>(q.label),
                          parent_question_id: new FormControl<string | null>(
                            q.parent_question_id || null
                          ),
                          children:
                            this.questionHierarchyService.recursiveFindAndGroupChildren(
                              theme.questions,
                              q.question_id
                            ),
                        })
                    )
                ),
              })
          ),
          [
            Validators.required,
            Validators.min(1),
            this.themeValidatorService.validate(),
          ]
        ),
      }),
      segments: new FormControl<string[]>(sheet.opportunity_segment_ids, [
        Validators.required,
        Validators.min(1),
      ]),
      expertName: new FormControl(sheet.additional_export_headers.expert_name),
      expertBio: new FormControl(sheet.additional_export_headers.expert_bio),
      dateAndTimeOfCall: new FormControl(
        sheet.additional_export_headers.date_and_time_of_call
      ),
      geography: new FormControl(sheet.additional_export_headers.geography),
      profileType: new FormControl(
        sheet.additional_export_headers.profile_type
      ),
      interviewerName: new FormControl(
        sheet.additional_export_headers.interviewer_name
      ),
      notetakerName: new FormControl(
        sheet.additional_export_headers.notetaker_name
      ),
      recruitmentSource: new FormControl(
        sheet.additional_export_headers.respondent_source
      ),
      answerType: new FormControl(sheet.answer_format, Validators.required),
      suggestedQuestionThreshold: new FormControl<number>(
        sheet.suggested_question_threshold ?? 2,
        {
          nonNullable: true,
          validators: [Validators.min(2)],
        }
      ),
    });

    const thresholdControl =
      this.editFormGroup.controls.suggestedQuestionThreshold;

    thresholdControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((threshold) => {
          if (thresholdControl.invalid) {
            this.loadingSuggestedCount = false;
            return of(null);
          }

          this.loadingSuggestedCount = true;

          return this.dataCaptureService
            .getSuggestedQuestionCount(sheet.data_capture_sheet_id, threshold)
            .pipe(
              catchError(() => of(null)),
              finalize(() => {
                if (thresholdControl.value === threshold) {
                  this.loadingSuggestedCount = false;
                }
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.suggestedQuestionCount = response?.count ?? null;
      });
  }

  private buildDiscussionGuidePayload() {
    const editableQuestions = this.questionHierarchyService
      .flattenThemes(this.themesControl)
      .map((question) => ({
        ...question,
        question: question.question.trim(),
      }));

    const existingQuestionsById = new Map(
      this.sheet.discussion_guide.map((question) => [
        question.question_id,
        question,
      ])
    );

    const mergedEditableQuestions = editableQuestions.map((question) => ({
      ...existingQuestionsById.get(question.question_id),
      ...question,
    }));
    const preservedQuestions = this.sheet.discussion_guide.filter(
      (question) =>
        question.suggestion_status === 'suggested' ||
        question.suggestion_status === 'declined'
    );

    const themeOrder = [
      ...new Set([
        ...mergedEditableQuestions.map((question) => question.theme),
        ...preservedQuestions.map((question) => question.theme),
      ]),
    ];

    return themeOrder.flatMap((theme) => [
      ...mergedEditableQuestions.filter((question) => question.theme === theme),
      ...preservedQuestions.filter((question) => question.theme === theme),
    ]);
  }

  private groupQuestionsByTheme(questions: IDiscussionGuideQuestion[]) {
    const themes = questions.map((question) => question.theme);

    return [...new Set(themes)].map((theme) => ({
      theme,
      questions: questions.filter((question) => question.theme === theme),
    }));
  }

  private removeSuggestedQuestion(questionId: string) {
    this.suggestedQuestionsByTheme = Object.entries(
      this.suggestedQuestionsByTheme
    ).reduce<Record<string, IDiscussionGuideQuestion[]>>(
      (acc, [theme, questions]) => {
        acc[theme] = questions.filter((q) => q.question_id !== questionId);
        return acc;
      },
      {}
    );
  }

  private insertApprovedSuggestion(question: IDiscussionGuideQuestion) {
    const questionGroup = this.toQuestionFormGroup(question);

    if (!question.parent_question_id) {
      this.getOrCreateThemeGroup(question.theme).controls.questions.push(
        questionGroup
      );
      return;
    }

    const parentQuestion = this.findQuestionInThemes(
      question.parent_question_id
    );

    if (!parentQuestion) {
      return;
    }

    parentQuestion.controls.children.push(questionGroup);
  }

  private getOrCreateThemeGroup(themeName: string): FormGroupTheme {
    const existingThemeIndex = this.themesControl.controls.findIndex(
      (theme) => theme.controls.theme.value === themeName
    );

    if (existingThemeIndex > -1) {
      return this.themesControl.at(existingThemeIndex);
    }

    const themeGroup: FormGroupTheme = new FormGroup({
      theme: new FormControl<string>(themeName, { nonNullable: true }),
      questions: new FormArray<FormGroupQuestion>([]),
    });

    this.themesControl.push(themeGroup);

    return themeGroup;
  }

  private toQuestionFormGroup(
    question: IDiscussionGuideQuestion
  ): FormGroupQuestion {
    return new FormGroup({
      question_id: new FormControl<string>(question.question_id, {
        nonNullable: true,
      }),
      question: new FormControl<string>(question.question, {
        nonNullable: true,
      }),
      theme: new FormControl<string>(question.theme, { nonNullable: true }),
      label: new FormControl<string>(question.label || '', {
        nonNullable: true,
      }),
      parent_question_id: new FormControl<string | null>(
        question.parent_question_id || null
      ),
      children: new FormArray<FormGroupQuestion>([]),
    });
  }

  private findQuestionInThemes(questionId: string) {
    for (const theme of this.themesControl.controls) {
      const foundQuestion = this.findQuestionInArray(
        theme.controls.questions,
        questionId
      );

      if (foundQuestion) {
        return foundQuestion;
      }
    }

    return null;
  }

  private findQuestionInArray(
    questions: FormArray<FormGroupQuestion>,
    questionId: string
  ): FormGroupQuestion | null {
    for (const question of questions.controls) {
      if (question.controls.question_id.value === questionId) {
        return question;
      }

      const foundChild = this.findQuestionInArray(
        question.controls.children,
        questionId
      );

      if (foundChild) {
        return foundChild;
      }
    }

    return null;
  }
}
